/**
 * Navigation builder - Generates menu items from the route manifest.
 *
 * Menu items are derived from route nav metadata in the manifest,
 * ensuring navigation and routing stay in sync automatically.
 *
 * Section-level metadata (icons, display names, ordering) is defined
 * in SECTION_META. Individual menu items come from manifest.nav entries.
 */

import type { ComponentType } from 'react';
import { AccessTimeIcon as AccessTime, AccountBalanceIcon as AccountBalance, AnalyticsIcon as Analytics, AssessmentIcon as Assessment, AttachMoneyIcon as AttachMoney, CalendarMonthIcon as CalendarMonth, ContentCutIcon as ContentCut, CreditCardIcon as CreditCard, DashboardIcon as Dashboard, GavelIcon as Gavel, GroupIcon as Group, PersonIcon as Person, SettingsIcon as Settings, ShopIcon as Shop, StarIcon as Star, StorefrontIcon as Storefront, TrendingUpIcon as TrendingUp } from '@/shared/ui/mui';
import type { SvgIconProps } from '@/shared/ui/mui';
import type { MenuItem, UserType } from '@/app/layouts/side-menu/types';
import type { ActorRole } from '../access/types';
import { manifest } from '../manifest';

// ============================================================================
// Icon resolution
// ============================================================================

/** Map icon names from manifest to actual MUI icon components */
const ICON_MAP: Record<string, ComponentType<SvgIconProps>> = {
  Dashboard,
  Group,
  AccessTime,
  CalendarMonth,
  ContentCut,
  Star,
  Assessment,
  AttachMoney,
  Analytics,
  AccountBalance,
  CreditCard,
  Person,
  Gavel,
  Settings,
  Shop,
  Storefront,
  TrendingUp,
};

const resolveIcon = (iconName: string): ComponentType<SvgIconProps> => {
  return ICON_MAP[iconName] || Dashboard;
};

// ============================================================================
// i18n label keys
// ============================================================================

/**
 * Map English manifest nav labels to keys in the `navigation` namespace.
 * The English `text` is preserved on each MenuItem (used as a stable React key
 * and as the fallback); the SideMenu translates `labelKey` at render time.
 */
const NAV_ITEM_KEY: Record<string, string> = {
  Dashboard: 'items.dashboard',
  Appointments: 'items.appointments',
  Schedule: 'items.schedule',
  Availability: 'items.availability',
  Calendar: 'items.calendar',
  'Working Hours': 'items.workingHours',
  'Time Off': 'items.timeOff',
  'Pending Approvals': 'items.pendingApprovals',
  Clients: 'items.clients',
  'Client List': 'items.clientList',
  Services: 'items.services',
  'Service List': 'items.serviceList',
  'My Services': 'items.myServices',
  Reviews: 'items.reviews',
  Earnings: 'items.earnings',
  'My Earnings': 'items.myEarnings',
  Revenue: 'items.revenue',
  Performance: 'items.performance',
  Staff: 'items.staff',
  'Business Profile': 'items.businessProfile',
  'My Profile': 'items.myProfile',
  'Profile Settings': 'items.profileSettings',
  'Booking Policies': 'items.bookingPolicies',
  'Payout Settings': 'items.payoutSettings',
  'Plan & Billing': 'items.planBilling',
  'Profile & Security': 'items.profileSecurity',
  'Shops & Locations': 'items.shopsLocations',
};

/** Map section header labels (from SECTION_META.label) to `navigation` keys. */
const NAV_SECTION_KEY: Record<string, string> = {
  Bookings: 'sections.bookings',
  Calendar: 'sections.calendar',
  Clients: 'sections.clients',
  Services: 'sections.services',
  Analytics: 'sections.analytics',
  Finance: 'sections.payments',
  Payments: 'sections.payments',
  Business: 'sections.business',
  Settings: 'sections.account',
  Account: 'sections.account',
};

/** i18n key for a role display name, in the `navigation` namespace. */
export const getRoleLabelKey = (role: UserType): string => {
  const map: Record<UserType, string> = {
    SHOP_OWNER: 'roles.shopOwner',
    INDEPENDENT_PROVIDER: 'roles.independentProvider',
    STAFF_PROVIDER: 'roles.staffProvider',
    USER: 'roles.client',
  };
  return map[role] ?? 'roles.client';
};

// ============================================================================
// Role mapping
// ============================================================================

const USER_TYPE_TO_ROLE: Record<UserType, ActorRole> = {
  SHOP_OWNER: 'shop_owner',
  STAFF_PROVIDER: 'staff_provider',
  INDEPENDENT_PROVIDER: 'independent_provider',
  USER: 'client',
};

const ROLE_TO_USER_TYPE: Record<ActorRole, UserType> = {
  shop_owner: 'SHOP_OWNER',
  shop_manager: 'SHOP_OWNER',
  staff_provider: 'STAFF_PROVIDER',
  independent_provider: 'INDEPENDENT_PROVIDER',
  client: 'USER',
};

// ============================================================================
// Section metadata - defines parent section icons, labels, and ordering
// ============================================================================

interface SectionMeta {
  icon: ComponentType<SvgIconProps>;
  label: string;
  order: number;
}

/**
 * Section-level metadata for nav groups.
 * Routes with nav.parent set are grouped under these sections.
 * Routes without nav.parent are rendered as standalone top-level items.
 */
const SECTION_META: Record<string, SectionMeta> = {
  // Shop sections
  Bookings: { icon: CalendarMonth, label: 'Bookings', order: 2 },
  Calendar: { icon: CalendarMonth, label: 'Calendar', order: 2 },
  Clients: { icon: Group, label: 'Clients', order: 3 },
  Services: { icon: ContentCut, label: 'Services', order: 4 },
  Analytics: { icon: Analytics, label: 'Analytics', order: 5 },
  Finance: { icon: AttachMoney, label: 'Payments', order: 6 },
  Business: { icon: Shop, label: 'Business', order: 7 },
  Settings: { icon: Settings, label: 'Account', order: 8 },
};

// ============================================================================
// Extra nav items not in the manifest (non-route navigation entries)
// ============================================================================

/**
 * Items that appear in navigation but don't correspond to routes in the manifest.
 * These are kept minimal - ideally they'd be added to the manifest as well.
 */
interface ExtraNavItem {
  icon: ComponentType<SvgIconProps>;
  text: string;
  path: string;
  parent: string;
  order: number;
  visibleFor: ActorRole[];
}

const EXTRA_NAV_ITEMS: ExtraNavItem[] = [
  // "Shops & Locations" in Business section for shop owners/managers
  {
    icon: Storefront,
    text: 'Shops & Locations',
    path: '/provider/select-shop',
    parent: 'Business',
    order: 0,
    visibleFor: ['shop_owner', 'shop_manager'],
  },
  // Payout Settings under Finance for shop owners/managers (same route as staff,
  // different menu placement — staff sees it top-level, owners under Finance)
  {
    icon: AccountBalance,
    text: 'Payout Settings',
    path: '/shops/:slug/payments',
    parent: 'Finance',
    order: 2,
    visibleFor: ['shop_owner', 'shop_manager'],
  },
];

// ============================================================================
// Path resolution helpers
// ============================================================================

/**
 * Resolve a manifest route path to an actual navigable path.
 * Replaces :slug with the actual shop slug for shop routes.
 */
const resolvePath = (routePath: string, shopSlug?: string): string => {
  if (routePath.includes(':slug')) {
    return shopSlug
      ? routePath.replace(':slug', shopSlug)
      : routePath.replace(':slug', 'unknown');
  }
  return routePath;
};

// ============================================================================
// Core manifest-driven builder
// ============================================================================

interface NavEntry {
  icon: ComponentType<SvgIconProps>;
  text: string;
  path: string;
  parent?: string;
  order: number;
  roles: UserType[];
}

const HIDDEN_NAV_PARENTS = new Set(['Analytics']);

/**
 * Build flat nav entries from the manifest for a given role and workspace.
 */
const buildEntriesFromManifest = (
  role: ActorRole,
  workspace: string,
  shopSlug?: string,
): NavEntry[] => {
  const entries: NavEntry[] = [];

  for (const route of manifest) {
    const { nav, access, path: routePath } = route;
    if (!nav) continue;
    if (access.workspace !== workspace) continue;
    if (!nav.visibleFor.includes(role)) continue;
    if (nav.parent && HIDDEN_NAV_PARENTS.has(nav.parent)) continue;

    entries.push({
      icon: resolveIcon(nav.icon),
      text: nav.label,
      path: resolvePath(routePath, shopSlug),
      parent: nav.parent,
      order: nav.order,
      roles: nav.visibleFor.map(r => ROLE_TO_USER_TYPE[r]),
    });
  }

  // Add extra nav items visible for this role
  for (const extra of EXTRA_NAV_ITEMS) {
    if (extra.visibleFor.includes(role)) {
      entries.push({
        icon: extra.icon,
        text: extra.text,
        path: resolvePath(extra.path, shopSlug),
        parent: extra.parent,
        order: extra.order,
        roles: extra.visibleFor.map(r => ROLE_TO_USER_TYPE[r]),
      });
    }
  }

  return entries;
};

/**
 * Assemble flat entries into hierarchical MenuItem[] structure.
 * Top-level items (no parent) become standalone menu items.
 * Items with the same parent are grouped under a section header.
 */
const assembleMenuTree = (entries: NavEntry[]): MenuItem[] => {
  const topLevel: NavEntry[] = [];
  const grouped = new Map<string, NavEntry[]>();

  for (const entry of entries) {
    if (entry.parent) {
      const group = grouped.get(entry.parent) || [];
      group.push(entry);
      grouped.set(entry.parent, group);
    } else {
      topLevel.push(entry);
    }
  }

  // Sort within each group
  for (const group of grouped.values()) {
    group.sort((a, b) => a.order - b.order);
  }

  // Build final menu items: top-level items interspersed with section groups
  // The ordering follows section meta order for groups, and nav.order for top-level
  const items: { sortOrder: number; menuItem: MenuItem }[] = [];

  // Add top-level items
  for (const entry of topLevel) {
    items.push({
      sortOrder: entry.order,
      menuItem: {
        icon: entry.icon,
        text: entry.text,
        labelKey: NAV_ITEM_KEY[entry.text],
        path: entry.path,
        roles: entry.roles,
      },
    });
  }

  // Add grouped sections
  for (const [parentName, children] of grouped.entries()) {
    const meta = SECTION_META[parentName];
    const sectionOrder = meta?.order ?? 99;
    const sectionIcon = meta?.icon ?? Dashboard;
    const sectionLabel = meta?.label ?? parentName;

    items.push({
      sortOrder: sectionOrder,
      menuItem: {
        icon: sectionIcon,
        text: sectionLabel,
        labelKey: NAV_SECTION_KEY[parentName],
        roles: children.flatMap(c => c.roles).filter((v, i, a) => a.indexOf(v) === i),
        subItems: children.map(child => ({
          icon: child.icon,
          text: child.text,
          labelKey: NAV_ITEM_KEY[child.text],
          path: child.path,
          roles: child.roles,
        })),
      },
    });
  }

  // Sort by section order
  items.sort((a, b) => a.sortOrder - b.sortOrder);

  return items.map(i => i.menuItem);
};

// ============================================================================
// Public API
// ============================================================================

/**
 * Options for building navigation items
 */
interface BuildNavOptions {
  /** Whether staff has permission to manage services */
  hasServicePermission?: boolean;
}

/**
 * Build navigation items for a given role and workspace context.
 *
 * Reads from the route manifest to produce MenuItem[] matching the
 * structure the SideMenu and NavigationContent components expect.
 *
 * @param userType - The user's role as UserType
 * @param shopSlug - Optional shop slug for shop-specific paths
 * @param options - Additional options like service permission for staff
 * @returns MenuItem[] for the SideMenu component
 */
export const buildNavItems = (
  userType: UserType,
  shopSlug?: string,
  options?: BuildNavOptions,
): MenuItem[] => {
  const role = USER_TYPE_TO_ROLE[userType];

  // Determine workspace based on role
  let workspace: string;
  switch (userType) {
    case 'SHOP_OWNER':
    case 'STAFF_PROVIDER':
      workspace = 'shop';
      break;
    case 'INDEPENDENT_PROVIDER':
      workspace = 'solo';
      break;
    case 'USER':
      workspace = 'none'; // client routes have no workspace set, handled below
      break;
    default:
      return [];
  }

  // Client routes don't use workspace in the manifest, filter by actor role instead
  let entries: NavEntry[];
  if (userType === 'USER') {
    entries = [];
    for (const route of manifest) {
      const { nav, access } = route;
      if (!nav) continue;
      if (!nav.visibleFor.includes(role)) continue;
      if (!access.actors?.includes(role)) continue;
      if (nav.parent && HIDDEN_NAV_PARENTS.has(nav.parent)) continue;
      entries.push({
        icon: resolveIcon(nav.icon),
        text: nav.label,
        path: route.path,
        parent: nav.parent,
        order: nav.order,
        roles: nav.visibleFor.map(r => ROLE_TO_USER_TYPE[r]),
      });
    }
  } else {
    entries = buildEntriesFromManifest(role, workspace, shopSlug);
  }

  let items = assembleMenuTree(entries);

  // Inject "My Services" for staff with service permission
  if (userType === 'STAFF_PROVIDER' && options?.hasServicePermission) {
    items = injectStaffServicesItem(items, shopSlug);
  }

  // Solo providers get account settings in their Account section
  if (userType === 'INDEPENDENT_PROVIDER') {
    const settingsSection = items.find(i => i.text === 'Account');
    if (settingsSection?.subItems) {
      settingsSection.subItems.push({
        icon: Settings,
        text: 'Profile & Security',
        labelKey: NAV_ITEM_KEY['Profile & Security'],
        path: '/settings',
        roles: ['INDEPENDENT_PROVIDER'],
      });
    }
  }

  return items;
};

/**
 * Inject "My Services" nav item for staff providers with service management permission.
 * Inserts after "Clients" and before "Payouts" in the menu.
 */
const injectStaffServicesItem = (items: MenuItem[], shopSlug?: string): MenuItem[] => {
  const clientsIndex = items.findIndex(item => item.text === 'Clients');
  const insertIndex = clientsIndex >= 0 ? clientsIndex + 1 : items.length - 1;

  const servicesPath = shopSlug ? `/shops/${shopSlug}/services` : '/solo/services';
  const servicesItem: MenuItem = {
    icon: ContentCut,
    text: 'My Services',
    labelKey: NAV_ITEM_KEY['My Services'],
    path: servicesPath,
    roles: ['STAFF_PROVIDER'] as UserType[],
  };

  return [
    ...items.slice(0, insertIndex),
    servicesItem,
    ...items.slice(insertIndex),
  ];
};

/**
 * Common menu items for all users (except solo providers who have it in their Settings section)
 */
export const COMMON_NAV_ITEMS: MenuItem[] = [
  {
    icon: Settings,
    text: 'Profile & Security',
    labelKey: NAV_ITEM_KEY['Profile & Security'],
    path: '/settings',
    roles: ['SHOP_OWNER', 'STAFF_PROVIDER', 'USER'] as UserType[],
  },
];

/**
 * Get role display name
 */
export const getRoleDisplay = (role: UserType): string => {
  const roleMap: Record<UserType, string> = {
    SHOP_OWNER: 'Shop Owner',
    INDEPENDENT_PROVIDER: 'Independent Provider',
    STAFF_PROVIDER: 'Staff Provider',
    USER: 'Client',
  };
  return roleMap[role] || 'User';
};
